import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FolderOpen, FilePlus,
  User, LogOut, MapPinned,
  ChevronLeft, ChevronRight, Menu, X,
  Shield, Globe
} from 'lucide-react';
import useAuthStore     from '../store/authStore';
import NotificationBell from './NotificationBell';
import useWebSocket     from '../hooks/useWebSocket';

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0',
  sandDark:  '#e8d8c0',
  terra:     '#c2521a',
  terraLt:   '#e0784a',
  terraDk:   '#8a3510',
  forest:    '#1a3d28',
  forestMid: '#244d35',
  forestLt:  '#2f6645',
  gold:      '#c8902a',
  goldLt:    '#e8b84a',
  ink:       '#0e1a10',
  mist:      '#6e8070',
  cream:     '#faf6ef',
};

/* ─── MENU ─── */
const MENU = [
  { label: 'Dashboard',       icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Mes dossiers',    icon: FolderOpen,      path: '/dossiers' },
  { label: 'Nouveau dossier', icon: FilePlus,        path: '/dossiers/nouveau' },
  { label: 'Mon profil',      icon: User,            path: '/profil' },
];

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root { height: 100%; }
body { font-family: 'DM Sans', sans-serif; background: #f2ebe0; color: ${C.forest}; overflow-x: hidden; }

/* ── SHELL ── */
.shell {
  display: flex;
  min-height: 100vh;
}

/* ── SIDEBAR ── */
.sidebar {
  position: fixed;
  top: 0; left: 0; bottom: 0;
  background: ${C.forest};
  display: flex;
  flex-direction: column;
  z-index: 50;
  transition: width .32s cubic-bezier(.22,1,.36,1);
  overflow: hidden;
  box-shadow: 4px 0 32px rgba(14,26,16,.18);
}
.sidebar.expanded { width: 256px; }
.sidebar.collapsed { width: 72px; }

/* sidebar top pattern */
.sidebar-pattern {
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, rgba(245,237,224,.025) 0, rgba(245,237,224,.025) 1px, transparent 1px, transparent 52px),
    repeating-linear-gradient(90deg, rgba(245,237,224,.025) 0, rgba(245,237,224,.025) 1px, transparent 1px, transparent 52px);
  pointer-events: none;
}
.sidebar-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
  width: 200px; height: 200px;
  top: -60px; right: -60px;
  background: radial-gradient(circle, rgba(194,82,26,.18), transparent 70%);
}
.sidebar-orb2 {
  position: absolute;
  border-radius: 50%;
  filter: blur(50px);
  pointer-events: none;
  width: 160px; height: 160px;
  bottom: 40px; left: -40px;
  background: radial-gradient(circle, rgba(47,102,69,.25), transparent 70%);
}

/* logo zone */
.sidebar-logo {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 20px 18px;
  text-decoration: none;
  flex: none;
  border-bottom: 1px solid rgba(245,237,224,.07);
  min-height: 72px;
}
.sidebar-logo-mark {
  width: 38px; height: 38px;
  border-radius: 11px;
  background: rgba(245,237,224,.1);
  border: 1px solid rgba(245,237,224,.14);
  display: grid; place-items: center;
  flex: none;
  transition: background .2s;
}
.sidebar-logo-mark:hover { background: rgba(245,237,224,.15); }
.sidebar-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${C.sand};
  letter-spacing: -.02em;
  white-space: nowrap;
  overflow: hidden;
}
.sidebar-logo-text em { color: ${C.terraLt}; font-style: normal; }

/* nav */
.sidebar-nav {
  position: relative;
  z-index: 2;
  flex: 1;
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  overflow-x: hidden;
}
.sidebar-nav::-webkit-scrollbar { width: 0; }

.nav-group-label {
  font-size: .6rem;
  font-weight: 700;
  letter-spacing: .2em;
  text-transform: uppercase;
  color: rgba(245,237,224,.25);
  padding: 10px 10px 6px;
  white-space: nowrap;
  overflow: hidden;
  transition: opacity .2s;
}
.sidebar.collapsed .nav-group-label { opacity: 0; }

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  transition: background .2s;
  white-space: nowrap;
  min-height: 46px;
  border: 1px solid transparent;
}
.nav-item:hover {
  background: rgba(245,237,224,.07);
  border-color: rgba(245,237,224,.06);
}
.nav-item.active {
  background: rgba(245,237,224,.1);
  border-color: rgba(245,237,224,.1);
}
.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0; top: 20%; bottom: 20%;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${C.terraLt};
}
.nav-icon {
  width: 22px; height: 22px;
  flex: none;
  color: rgba(245,237,224,.5);
  transition: color .2s;
}
.nav-item:hover .nav-icon,
.nav-item.active .nav-icon { color: ${C.sand}; }
.nav-item.active .nav-icon { color: ${C.terraLt}; }

.nav-label {
  font-size: .9rem;
  font-weight: 500;
  color: rgba(245,237,224,.6);
  overflow: hidden;
  white-space: nowrap;
  transition: color .2s, opacity .2s, width .32s;
}
.nav-item:hover .nav-label,
.nav-item.active .nav-label { color: ${C.sand}; }
.sidebar.collapsed .nav-label { width: 0; opacity: 0; pointer-events: none; }

/* active pill */
.nav-item .active-pip {
  margin-left: auto;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${C.terraLt};
  flex: none;
  opacity: 0;
  transition: opacity .2s;
}
.nav-item.active .active-pip { opacity: 1; }
.sidebar.collapsed .active-pip { display: none; }

/* tooltip on collapsed */
.nav-tooltip {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%; transform: translateY(-50%);
  background: ${C.ink};
  color: ${C.sand};
  font-size: .78rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity .15s;
  box-shadow: 0 4px 16px rgba(0,0,0,.25);
  z-index: 100;
}
.nav-tooltip::before {
  content: '';
  position: absolute;
  right: 100%; top: 50%; transform: translateY(-50%);
  border: 5px solid transparent;
  border-right-color: ${C.ink};
}
.sidebar.collapsed .nav-item:hover .nav-tooltip { opacity: 1; }

/* divider */
.sidebar-divider {
  position: relative;
  z-index: 2;
  height: 1px;
  background: rgba(245,237,224,.07);
  margin: 4px 10px;
}

/* user zone */
.sidebar-user {
  position: relative;
  z-index: 2;
  padding: 14px 12px;
  border-top: 1px solid rgba(245,237,224,.07);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background .2s;
  border-radius: 0;
  overflow: hidden;
  flex: none;
}
.sidebar-user:hover { background: rgba(245,237,224,.05); }
.user-avatar {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${C.terra}, ${C.gold});
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: 700;
  color: white;
  flex: none;
}
.user-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  transition: opacity .2s, width .32s;
}
.sidebar.collapsed .user-info { width: 0; opacity: 0; }
.user-name {
  font-size: .85rem;
  font-weight: 600;
  color: ${C.sand};
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-role {
  font-size: .72rem;
  color: rgba(245,237,224,.35);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
}

/* collapse toggle */
.sidebar-toggle {
  position: fixed;
  left: 244px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 51;
  width: 24px; height: 24px;
  border-radius: 50%;
  background: ${C.cream};
  border: 1.5px solid rgba(26,61,40,.15);
  display: grid; place-items: center;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(26,61,40,.12);
  transition: left .32s cubic-bezier(.22,1,.36,1), background .2s;
  color: ${C.mist};
}
.sidebar-toggle.collapsed { left: 60px; }
.sidebar-toggle:hover { background: white; color: ${C.forest}; }

/* ── TOPBAR ── */
.topbar {
  position: fixed;
  top: 0; right: 0;
  height: 64px;
  background: rgba(250,246,239,.9);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(26,61,40,.07);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 28px;
  z-index: 40;
  transition: left .32s cubic-bezier(.22,1,.36,1);
  box-shadow: 0 2px 20px rgba(26,61,40,.05);
}

/* breadcrumb */
.topbar-breadcrumb {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.breadcrumb-home {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1rem;
  font-weight: 600;
  color: ${C.mist};
  text-decoration: none;
  transition: color .2s;
}
.breadcrumb-home:hover { color: ${C.forest}; }
.breadcrumb-sep { color: rgba(110,128,112,.35); font-size: .9rem; }
.breadcrumb-current {
  font-size: .92rem;
  font-weight: 600;
  color: ${C.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* topbar actions */
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
}
.topbar-icon-btn {
  width: 38px; height: 38px;
  border-radius: 11px;
  border: 1px solid rgba(26,61,40,.1);
  background: transparent;
  display: grid; place-items: center;
  cursor: pointer;
  color: ${C.mist};
  transition: background .2s, color .2s, border-color .2s;
  position: relative;
}
.topbar-icon-btn:hover {
  background: rgba(26,61,40,.05);
  color: ${C.forest};
  border-color: rgba(26,61,40,.18);
}

/* user chip topbar */
.topbar-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px 6px 6px;
  border-radius: 12px;
  border: 1px solid rgba(26,61,40,.1);
  background: white;
  cursor: pointer;
  transition: background .2s, box-shadow .2s;
  box-shadow: 0 2px 8px rgba(26,61,40,.05);
  text-decoration: none;
}
.topbar-user:hover { background: ${C.cream}; box-shadow: 0 4px 16px rgba(26,61,40,.1); }
.topbar-avatar {
  width: 30px; height: 30px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${C.terra}, ${C.gold});
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: .95rem;
  font-weight: 700;
  color: white;
  flex: none;
}
.topbar-username {
  font-size: .85rem;
  font-weight: 600;
  color: ${C.ink};
  white-space: nowrap;
}
.topbar-role {
  font-size: .7rem;
  color: ${C.mist};
  margin-top: 1px;
}

.topbar-logout {
  width: 38px; height: 38px;
  border-radius: 11px;
  border: 1px solid rgba(194,82,26,.18);
  background: rgba(194,82,26,.05);
  display: grid; place-items: center;
  cursor: pointer;
  color: ${C.terra};
  transition: background .2s, color .2s;
}
.topbar-logout:hover { background: rgba(194,82,26,.1); color: ${C.terraDk}; }

/* status indicator */
.status-indicator {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(47,102,69,.08);
  border: 1px solid rgba(47,102,69,.15);
  font-size: .74rem;
  font-weight: 600;
  color: ${C.forestMid};
}
.status-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${C.forestLt};
  animation: blink 2.4s ease-in-out infinite;
}
@keyframes blink {
  0%,100% { opacity: 1; }
  50%      { opacity: .35; }
}

/* ── MAIN ── */
.main-content {
  flex: 1;
  min-height: 100vh;
  transition: margin-left .32s cubic-bezier(.22,1,.36,1);
  padding-top: 64px;
  background:
    radial-gradient(ellipse at 80% 0%, rgba(194,82,26,.055) 0%, transparent 50%),
    radial-gradient(ellipse at 0% 80%, rgba(47,102,69,.04) 0%, transparent 50%),
    #f2ebe0;
}

/* ── MOBILE OVERLAY ── */
.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(14,26,16,.5);
  z-index: 49;
  backdrop-filter: blur(2px);
}
@media (max-width: 768px) {
  .sidebar { width: 256px !important; transform: translateX(-100%); transition: transform .32s cubic-bezier(.22,1,.36,1), width .32s !important; }
  .sidebar.mobile-open { transform: translateX(0); }
  .sidebar-toggle { display: none; }
  .main-content { margin-left: 0 !important; }
  .topbar { left: 0 !important; }
  .mobile-overlay.visible { display: block; }
  .mobile-menu-btn { display: flex !important; }
  .topbar-user .topbar-username,
  .topbar-user .topbar-role { display: none; }
  .status-indicator { display: none; }
}
.mobile-menu-btn {
  display: none;
  width: 38px; height: 38px;
  border-radius: 11px;
  border: 1px solid rgba(26,61,40,.12);
  background: transparent;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${C.mist};
  transition: background .2s;
}
.mobile-menu-btn:hover { background: rgba(26,61,40,.05); }
`;

/* ─── BREADCRUMB MAP ─── */
const LABELS = {
  '/dashboard':                'Dashboard',
  '/dossiers':                 'Mes dossiers',
  '/dossiers/nouveau':         'Nouveau dossier',
  '/profil':                   'Mon profil',
  '/rapports':                 'Rapports',
  '/settings':                 'Paramètres',
};

export default function Layout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useWebSocket();

  const handleLogout = () => { logout(); navigate('/login'); };

  const sidebarW = collapsed ? 72 : 256;
  const initials = ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || user?.username?.[0] || 'U')).toUpperCase() || 'TT';

  // breadcrumb
  const segments = location.pathname.split('/').filter(Boolean);
  const currentLabel = LABELS[location.pathname]
    || (segments.length ? segments[segments.length - 1].replace(/-/g, ' ') : 'Dashboard');

  return (
    <>
      <style>{css}</style>

      {/* ── SIDEBAR ── */}
      <motion.aside
        className={`sidebar ${collapsed ? 'collapsed' : 'expanded'} ${mobileOpen ? 'mobile-open' : ''}`}
        style={{ width: sidebarW }}>
        <div className="sidebar-pattern" />
        <div className="sidebar-orb" />
        <div className="sidebar-orb2" />

        {/* logo */}
        <a href="/" className="sidebar-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <div className="sidebar-logo-mark">
            <MapPinned size={18} color="rgba(245,237,224,.8)" />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.div className="sidebar-logo-text"
                initial={{ opacity:0, width:0 }} animate={{ opacity:1, width:'auto' }}
                exit={{ opacity:0, width:0 }} transition={{ duration:.22 }}>
                Terra<em>Track</em>
              </motion.div>
            )}
          </AnimatePresence>
        </a>

        {/* nav */}
        <nav className="sidebar-nav">
          <div className="nav-group-label">Navigation</div>

          {MENU.map(({ label, icon: Icon, path }) => {
            const active = location.pathname === path;
            return (
              <a key={path} className={`nav-item ${active ? 'active' : ''}`}
                href={path}
                onClick={e => { e.preventDefault(); navigate(path); setMobileOpen(false); }}>
                <Icon size={18} className="nav-icon" />
                <span className="nav-label">{label}</span>
                <span className="active-pip" />
                {collapsed && <span className="nav-tooltip">{label}</span>}
              </a>
            );
          })}

          <div className="sidebar-divider" style={{ margin:'12px 0' }} />
          <div className="nav-group-label">Système</div>

          {[
            { label:'Sécurité', icon: Shield, path:'/settings' },
            { label:'Pays couverts', icon: Globe, path:'/pays' },
          ].map(({ label, icon: Icon, path }) => (
            <a key={path} className={`nav-item ${location.pathname === path ? 'active' : ''}`}
              href={path}
              onClick={e => { e.preventDefault(); navigate(path); setMobileOpen(false); }}>
              <Icon size={18} className="nav-icon" />
              <span className="nav-label">{label}</span>
              {collapsed && <span className="nav-tooltip">{label}</span>}
            </a>
          ))}
        </nav>

        {/* user */}
        <div className="sidebar-user" onClick={() => navigate('/profil')}>
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <div className="user-name">{user?.first_name} {user?.last_name || user?.username}</div>
            <div className="user-role">{user?.role || 'Membre'}</div>
          </div>
        </div>
      </motion.aside>

      {/* ── TOGGLE BUTTON ── */}
      <button
        className={`sidebar-toggle ${collapsed ? 'collapsed' : ''}`}
        onClick={() => setCollapsed(v => !v)}
        style={{ left: collapsed ? 60 : 244 }}
        title={collapsed ? 'Étendre' : 'Réduire'}>
        {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
      </button>

      {/* ── MOBILE OVERLAY ── */}
      <div
        className={`mobile-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* ── TOPBAR ── */}
      <header
        className="topbar"
        style={{ left: sidebarW }}>

        {/* mobile menu */}
        <button className="mobile-menu-btn" onClick={() => setMobileOpen(v => !v)}>
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        {/* breadcrumb */}
        <div className="topbar-breadcrumb">
          <a href="/" className="breadcrumb-home" onClick={e => { e.preventDefault(); navigate('/'); }}>
            TerraTrack
          </a>
          {location.pathname !== '/' && (
            <>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-current">{currentLabel}</span>
            </>
          )}
        </div>

        {/* status */}
        <div className="status-indicator">
          <span className="status-dot" />
          Système actif
        </div>

        {/* actions */}
        <div className="topbar-actions">
          <NotificationBell className="topbar-icon-btn" />

          <a href="/profil" className="topbar-user" onClick={e => { e.preventDefault(); navigate('/profil'); }}>
            <div className="topbar-avatar">{initials}</div>
            <div>
              <div className="topbar-username">{user?.first_name || user?.username}</div>
              <div className="topbar-role">{user?.role || 'Membre'}</div>
            </div>
          </a>

          <button className="topbar-logout" onClick={handleLogout} title="Déconnexion">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main
        className="main-content"
        style={{ marginLeft: sidebarW }}>
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: .35, ease: [.22, 1, .36, 1] }}>
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </>
  );
}